import React, {useEffect, useState} from "react";
import {Checkbox, Form, Input, Switch, Table, Button} from "antd";
import {shiftOptionTypes} from "./ShiftOptionTypes";
import {useStore} from "../../../../hooks/useStore";
import {useTranslation} from "react-i18next";
import {FormikProvider, getIn, useFormik} from "formik";
import {ErrorFocus} from "../../../_defaultComponents/ErrorFocus/ErrorFocus";
import {
    CompanyButtonContainer,
    MergeOptionsTableWrap,
    MergeOptionsList,
    MergeOptionsListRow
} from "../optionsStyles";
import {handleCheckBoxText} from "../../../../utils/helpers";
import {shiftOptionsValidation} from "../shiftOptionsValidation";
import {EmptyMessage} from "../../../_defaultComponents/EmptyMessage/EmptyMessage";

const Options = ({
    companyLoading,
    setCompanyLoading,
    companyData,
    searchText,
    city,
    setSubCompanyRecall,
    setCompanyRecall,
    companyRecallRef,
    subCompanyRecallRef
}: shiftOptionTypes) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const shift = companyData.data?.options?.shift || {};

    const formik = useFormik({
        initialValues: {
            options: {
                shift: {
                    shiftControlIsActive: shift.shiftControlIsActive || false,
                    earliestShiftStart: shift.earliestShiftStart || 1,
                    latestShiftEnd: shift.latestShiftEnd || 1,
                    distanceAlertIsActive: shift.distanceAlertIsActive || false,
                    distanceLimit: shift.distanceLimit || 1,
                    maxShiftPackageLimit: shift.maxShiftPackageLimit || 1
                },
                ...companyData.data?.options
            },
            applyShiftOptions: false,
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        validationSchema: shiftOptionsValidation,
        onSubmit: async values => {
            try {
                setCompanyLoading(true);

                let filters = {};
                filters.searchText = searchText;
                filters.city = city;
                if (Object.keys(filters).length > 0) {
                    values.filters = filters;
                }
                if (values.isActive) {
                    values.applyShiftOptions = true;
                }

                await stores.companyStore.updateCompanyDistanceNotificationOptions(values);
                if (values.isActive) {
                    setSubCompanyRecall(!subCompanyRecallRef.current);
                }
                setCompanyRecall(!companyRecallRef.current);
                setCompanyLoading(false);
            } catch (e) {
                console.log(e);
            }
        }
    });

    const {values, errors, touched, handleSubmit, isSubmitting, setFieldValue} = formik;
    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);

    const renderSwitch = key => {
        return (
            <Table.Column
                title={`${t(`fields.shiftOptions.${key}.label`, {
                    ns: "common"
                })} (${t(`fields.shiftOptions.${key}.short`, {ns: "common"})})`}
                dataIndex="isActive"
                render={() => (
                    <Form.Item>
                        <Switch
                            checked={values.options.shift && values.options.shift[key]}
                            checkedChildren={t("other.active", {ns: "common"})}
                            unCheckedChildren={t("other.passive", {ns: "common"})}
                            data-testid={key}
                            onChange={e => {
                                setFieldValue(`options.shift.${key}`, e);
                                setIsCompanyFormChanged(true);
                            }}
                        />
                    </Form.Item>
                )}
                align="center"
            />
        );
    };

    const renderInput = (key, disableKey) => {
        const getInErrors = getIn(errors, `options.shift.${key}`);
        const getInTouch = getIn(touched, `options.shift.${key}`);
        const hasError = getInErrors && getInTouch;
        return (
            <Table.Column
                title={`${t(`fields.shiftOptions.${key}.label`, {
                    ns: "common"
                })} (${t(`fields.shiftOptions.${key}.short`, {ns: "common"})})`}
                dataIndex="limit"
                render={() => (
                    <Form.Item
                        validateStatus={hasError ? "error" : "success"}
                        help={hasError ? getInErrors : null}
                    >
                        <Input
                            type="number"
                            name="limit"
                            data-testid="limitInput"
                            value={
                                values.options.shift && values.options.shift[key]
                                    ? values.options.shift[key]
                                    : shift && shift[key]
                            }
                            onChange={e =>
                                setFieldValue(`options.shift.${key}`, e.target.value || "")
                            }
                            disabled={!values.options.shift || !values.options.shift[disableKey]}
                        />
                    </Form.Item>
                )}
                align="center"
            />
        );
    };

    return (
        <Form
            autoComplete="off"
            onFinish={handleSubmit}
            data-testid="appointmentOptionsForm"
            onChange={() => setIsCompanyFormChanged(true)}
        >
            <ErrorFocus formik={formik} />
            <FormikProvider value={formik}>
            <MergeOptionsTableWrap>
            <Table
                loading={companyLoading}
                dataSource={[companyData.data]}
                rowKey="_id"
                pagination={false}
                data-testid="appointmentOptionsTable"
                locale={{
                    emptyText: <EmptyMessage />
                }}
            >
                {renderSwitch("shiftControlIsActive")}
                {renderInput("earliestShiftStart", "shiftControlIsActive")}
                {renderInput("latestShiftEnd", "shiftControlIsActive")}
                {renderSwitch("distanceAlertIsActive")}
                {renderInput("distanceLimit", "distanceAlertIsActive")}
                {renderSwitch("packageLimitAlertIsActive")}
                {renderInput("maxShiftPackageLimit", "packageLimitAlertIsActive")}
            </Table>
            </MergeOptionsTableWrap>

            <MergeOptionsList>
                <MergeOptionsListRow>
                    <span className="merge-options-label">
                        {t("fields.shiftOptions.shiftControlIsActive.label", {ns: "common"})} (
                        {t("fields.shiftOptions.shiftControlIsActive.short", {ns: "common"})})
                    </span>
                    <Form.Item>
                        <Switch
                            checked={values.options.shift?.shiftControlIsActive}
                            checkedChildren={t("other.active", {ns: "common"})}
                            unCheckedChildren={t("other.passive", {ns: "common"})}
                            onChange={e => {
                                setFieldValue("options.shift.shiftControlIsActive", e);
                                setIsCompanyFormChanged(true);
                            }}
                        />
                    </Form.Item>
                </MergeOptionsListRow>
                <MergeOptionsListRow>
                    <span className="merge-options-label">
                        {t("fields.shiftOptions.earliestShiftStart.label", {ns: "common"})} (
                        {t("fields.shiftOptions.earliestShiftStart.short", {ns: "common"})})
                    </span>
                    <Form.Item
                        validateStatus={
                            getIn(errors, "options.shift.earliestShiftStart") &&
                            getIn(touched, "options.shift.earliestShiftStart")
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, "options.shift.earliestShiftStart") &&
                            getIn(touched, "options.shift.earliestShiftStart")
                                ? getIn(errors, "options.shift.earliestShiftStart")
                                : null
                        }
                    >
                        <Input
                            type="number"
                            value={
                                values.options.shift?.shiftControlIsActive
                                    ? values.options.shift.earliestShiftStart
                                    : shift.earliestShiftStart
                            }
                            onChange={e =>
                                setFieldValue("options.shift.earliestShiftStart", e.target.value || "")
                            }
                            disabled={!values.options.shift?.shiftControlIsActive}
                        />
                    </Form.Item>
                </MergeOptionsListRow>
                <MergeOptionsListRow>
                    <span className="merge-options-label">
                        {t("fields.shiftOptions.latestShiftEnd.label", {ns: "common"})} (
                        {t("fields.shiftOptions.latestShiftEnd.short", {ns: "common"})})
                    </span>
                    <Form.Item
                        validateStatus={
                            getIn(errors, "options.shift.latestShiftEnd") &&
                            getIn(touched, "options.shift.latestShiftEnd")
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, "options.shift.latestShiftEnd") &&
                            getIn(touched, "options.shift.latestShiftEnd")
                                ? getIn(errors, "options.shift.latestShiftEnd")
                                : null
                        }
                    >
                        <Input
                            type="number"
                            value={
                                values.options.shift?.shiftControlIsActive
                                    ? values.options.shift.latestShiftEnd
                                    : shift.latestShiftEnd
                            }
                            onChange={e =>
                                setFieldValue("options.shift.latestShiftEnd", e.target.value || "")
                            }
                            disabled={!values.options.shift?.shiftControlIsActive}
                        />
                    </Form.Item>
                </MergeOptionsListRow>
                <MergeOptionsListRow>
                    <span className="merge-options-label">
                        {t("fields.shiftOptions.distanceAlertIsActive.label", {ns: "common"})} (
                        {t("fields.shiftOptions.distanceAlertIsActive.short", {ns: "common"})})
                    </span>
                    <Form.Item>
                        <Switch
                            checked={values.options.shift?.distanceAlertIsActive}
                            checkedChildren={t("other.active", {ns: "common"})}
                            unCheckedChildren={t("other.passive", {ns: "common"})}
                            onChange={e => {
                                setFieldValue("options.shift.distanceAlertIsActive", e);
                                setIsCompanyFormChanged(true);
                            }}
                        />
                    </Form.Item>
                </MergeOptionsListRow>
                <MergeOptionsListRow>
                    <span className="merge-options-label">
                        {t("fields.shiftOptions.distanceLimit.label", {ns: "common"})} (
                        {t("fields.shiftOptions.distanceLimit.short", {ns: "common"})})
                    </span>
                    <Form.Item
                        validateStatus={
                            getIn(errors, "options.shift.distanceLimit") &&
                            getIn(touched, "options.shift.distanceLimit")
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, "options.shift.distanceLimit") &&
                            getIn(touched, "options.shift.distanceLimit")
                                ? getIn(errors, "options.shift.distanceLimit")
                                : null
                        }
                    >
                        <Input
                            type="number"
                            value={
                                values.options.shift?.distanceAlertIsActive
                                    ? values.options.shift.distanceLimit
                                    : shift.distanceLimit
                            }
                            onChange={e =>
                                setFieldValue("options.shift.distanceLimit", e.target.value || "")
                            }
                            disabled={!values.options.shift?.distanceAlertIsActive}
                        />
                    </Form.Item>
                </MergeOptionsListRow>
                <MergeOptionsListRow>
                    <span className="merge-options-label">
                        {t("fields.shiftOptions.packageLimitAlertIsActive.label", {ns: "common"})} (
                        {t("fields.shiftOptions.packageLimitAlertIsActive.short", {ns: "common"})})
                    </span>
                    <Form.Item>
                        <Switch
                            checked={values.options.shift?.packageLimitAlertIsActive}
                            checkedChildren={t("other.active", {ns: "common"})}
                            unCheckedChildren={t("other.passive", {ns: "common"})}
                            onChange={e => {
                                setFieldValue("options.shift.packageLimitAlertIsActive", e);
                                setIsCompanyFormChanged(true);
                            }}
                        />
                    </Form.Item>
                </MergeOptionsListRow>
                <MergeOptionsListRow>
                    <span className="merge-options-label">
                        {t("fields.shiftOptions.maxShiftPackageLimit.label", {ns: "common"})} (
                        {t("fields.shiftOptions.maxShiftPackageLimit.short", {ns: "common"})})
                    </span>
                    <Form.Item
                        validateStatus={
                            getIn(errors, "options.shift.maxShiftPackageLimit") &&
                            getIn(touched, "options.shift.maxShiftPackageLimit")
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, "options.shift.maxShiftPackageLimit") &&
                            getIn(touched, "options.shift.maxShiftPackageLimit")
                                ? getIn(errors, "options.shift.maxShiftPackageLimit")
                                : null
                        }
                    >
                        <Input
                            type="number"
                            value={
                                values.options.shift?.packageLimitAlertIsActive
                                    ? values.options.shift.maxShiftPackageLimit
                                    : shift.maxShiftPackageLimit
                            }
                            onChange={e =>
                                setFieldValue("options.shift.maxShiftPackageLimit", e.target.value || "")
                            }
                            disabled={!values.options.shift?.packageLimitAlertIsActive}
                        />
                    </Form.Item>
                </MergeOptionsListRow>
            </MergeOptionsList>
            </FormikProvider>
            <CompanyButtonContainer justify="end" align="middle">
                <Checkbox
                    data-testid="applyForAllSubCompaniesCheckbox"
                    checked={values?.applyShiftOptions}
                    onChange={e => setFieldValue("applyShiftOptions", e.target.checked)}
                >
                    {handleCheckBoxText(searchText, city)}
                </Checkbox>
                <Button
                    htmlType="submit"
                    disabled={!isCompanyFormChanged || isSubmitting}
                    type="primary"
                    data-testid="submitButton"
                >
                    {t("actions.save", {ns: "common"})}
                </Button>
            </CompanyButtonContainer>
        </Form>
    );
};

export default Options;
